export const ADMIN_ROLE_CODES = {
  SUPER_ADMIN: 'SA',
  ADMIN: 'A',
  OPERATION: 'P',
  MARKETING: 'M',
  STAFF: 'S',
};

export const ADMIN_ROLE_NAMES = {
  [ADMIN_ROLE_CODES.SUPER_ADMIN]: 'Super Administrator',
  [ADMIN_ROLE_CODES.ADMIN]: 'Administrator',
  [ADMIN_ROLE_CODES.OPERATION]: 'Operation',
  [ADMIN_ROLE_CODES.MARKETING]: 'Marketing',
  [ADMIN_ROLE_CODES.STAFF]: 'Staff',
};

export const USER_ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  OPERATION: 'operation',
  MARKETING: 'marketing',
  STAFF: 'staff',
};

export const ROLE_LABELS = {
  [USER_ROLES.SUPER_ADMIN]: 'Super Administrator',
  [USER_ROLES.ADMIN]: 'Administrator (Read-Only)',
  [USER_ROLES.OPERATION]: 'Operations Manager',
  [USER_ROLES.MARKETING]: 'Marketing Manager',
  [USER_ROLES.STAFF]: 'Staff Member',
};

export const ROLE_DESCRIPTIONS = {
  [USER_ROLES.SUPER_ADMIN]:
    'Full system access including user management, invitations, and administrative privileges',
  [USER_ROLES.ADMIN]:
    'Read-only administrative access across system resources and user directories',
  [USER_ROLES.OPERATION]:
    'Operational management with content and user oversight',
  [USER_ROLES.MARKETING]:
    'Marketing focused access with campaign and analytics tools',
  [USER_ROLES.STAFF]: 'Standard staff level access for operational tasks',
};

export const ROLE_PERMISSIONS = {
  [USER_ROLES.SUPER_ADMIN]: [
    'users.create',
    'users.read',
    'users.update',
    'users.delete',
    'content.create',
    'content.read',
    'content.update',
    'content.delete',
    'settings.read',
    'settings.update',
    'analytics.read',
    'audit.read',
    'system.admin',
  ],
  [USER_ROLES.ADMIN]: [
    'users.read',
    'content.read',
    'settings.read',
    'analytics.read',
    'audit.read',
  ],
  [USER_ROLES.OPERATION]: [
    'users.read',
    'users.update',
    'content.create',
    'content.read',
    'content.update',
    'analytics.read',
    'notifications.manage',
  ],
  [USER_ROLES.MARKETING]: [
    'content.create',
    'content.read',
    'content.update',
    'analytics.read',
    'campaigns.manage',
    'social.manage',
  ],
  [USER_ROLES.STAFF]: ['users.read', 'content.read'],
};

export const ROLE_HIERARCHY = {
  [USER_ROLES.SUPER_ADMIN]: 4,
  [USER_ROLES.ADMIN]: 3,
  [USER_ROLES.OPERATION]: 2,
  [USER_ROLES.MARKETING]: 1,
  [USER_ROLES.STAFF]: 0,
};

export const isAdminOrAbove = (userOrRole) => {
  if (!userOrRole) return false;

  // If string (role name or code)
  if (typeof userOrRole === 'string') {
    const r = userOrRole.trim().toLowerCase();
    return (
      userOrRole === USER_ROLES.SUPER_ADMIN ||
      userOrRole === USER_ROLES.ADMIN ||
      userOrRole === ADMIN_ROLE_CODES.SUPER_ADMIN ||
      userOrRole === ADMIN_ROLE_CODES.ADMIN ||
      r === 'sa' ||
      r === 'a' ||
      r === 'super_admin' ||
      r === 'admin' ||
      r === 'super administrator' ||
      r === 'administrator'
    );
  }

  // If user object
  if (typeof userOrRole === 'object') {
    // Check direct role property
    if (userOrRole.role && isAdminOrAbove(userOrRole.role)) return true;

    // Check roles.admin array
    if (userOrRole.roles?.admin && Array.isArray(userOrRole.roles.admin)) {
      return userOrRole.roles.admin.some(r => isAdminOrAbove(r));
    }
  }

  return false;
};

export const hasMinRole = (userOrRole, minRole) => {
  if (!userOrRole || !minRole) return false;

  if (minRole === USER_ROLES.ADMIN || minRole === ADMIN_ROLE_CODES.ADMIN) {
    return isAdminOrAbove(userOrRole);
  }

  let role = typeof userOrRole === 'string' ? userOrRole : userOrRole.role;

  // If role is code like 'SA' or 'A', map to USER_ROLES
  if (role === ADMIN_ROLE_CODES.SUPER_ADMIN || role === 'SA') role = USER_ROLES.SUPER_ADMIN;
  else if (role === ADMIN_ROLE_CODES.ADMIN || role === 'A') role = USER_ROLES.ADMIN;
  else if (role === ADMIN_ROLE_CODES.OPERATION || role === 'P') role = USER_ROLES.OPERATION;
  else if (role === ADMIN_ROLE_CODES.MARKETING || role === 'M') role = USER_ROLES.MARKETING;
  else if (role === ADMIN_ROLE_CODES.STAFF || role === 'S') role = USER_ROLES.STAFF;

  // Fallback to checking user.roles.admin
  if (typeof userOrRole === 'object' && userOrRole.roles?.admin && Array.isArray(userOrRole.roles.admin)) {
    if (userOrRole.roles.admin.some(r => r === 'SA' || r === ADMIN_ROLE_CODES.SUPER_ADMIN)) {
      role = USER_ROLES.SUPER_ADMIN;
    } else if (userOrRole.roles.admin.some(r => r === 'A' || r === ADMIN_ROLE_CODES.ADMIN)) {
      if (role !== USER_ROLES.SUPER_ADMIN) role = USER_ROLES.ADMIN;
    }
  }

  const userLevel = ROLE_HIERARCHY[role] ?? -1;
  const minLevel = ROLE_HIERARCHY[minRole] ?? 999;

  return userLevel >= minLevel;
};

